/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après vérification). Daté au moment des faits : 2024-09-30.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "micronora-2024-microns-or-jumeau-numerique",
  "title": "Micronora 2024 : deux Microns d’Or bisontins, dont un jumeau numérique",
  "excerpt": "Le salon international des microtechniques annonçait plus de 800 exposants à Micropolis pour ses 75 ans. Son palmarès distingue deux entreprises bisontines, dont Percipio Robotics pour un jumeau numérique.",
  "seoTitle": "Micronora 2024 : deux Microns d’Or bisontins",
  "seoDescription": "Micronora 2024 annonçait plus de 800 exposants à Micropolis. Deux Microns d’Or bisontins : SilMach et Percipio Robotics, récompensée pour un jumeau numérique.",
  "category": "evenements",
  "tags": [
    "Micronora",
    "Micropolis",
    "Microns d'Or",
    "Percipio Robotics",
    "SilMach"
  ],
  "author": "jimmy",
  "publishedAt": "2024-09-30",
  "updatedAt": "2024-09-30",
  "featured": false,
  "cover": {
    "image": "/img/blog/micropolis-besancon-004.jpg",
    "credit": "Photo : Micropolis Besançon — Arnaud 25, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Visiteurs marchant vers l'entrée du hall E, reconnaissable à sa toiture conique vitrée, au parc des expositions Micropolis de Besançon un jour de manifestation."
  },
  "content": [
    {
      "type": "lead",
      "text": "Du 24 au 27 septembre 2024, le parc des expositions Micropolis a accueilli [Micronora](/blog/micronora-2026-salon-microtechniques-acteurs-ia), le salon international des microtechniques et de la précision, qui fêtait cette année ses 75 ans[^6]. Les organisateurs y annonçaient plus de 800 exposants, dont un tiers d’internationaux[^1]. Au-delà de la fréquentation, c’est le palmarès des Microns d’Or qui dit le plus de l’évolution du secteur : à côté d’un centre d’usinage et d’un micromoteur en silicium, un jumeau numérique a été récompensé[^3]."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Quoi",
          "value": "Micronora, salon international des microtechniques et de la précision"
        },
        {
          "label": "Quand",
          "value": "Du mardi 24 au vendredi 27 septembre 2024"
        },
        {
          "label": "Où",
          "value": "Parc des expositions Micropolis, Besançon"
        },
        {
          "label": "Qui",
          "value": "Plus de 800 exposants annoncés, dont 33 % d’internationaux, représentant 41 pays"
        },
        {
          "label": "Palmarès",
          "value": "Microns d’Or à Chiron, SilMach et Percipio Robotics"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Une édition placée sous le signe des microtechniques intelligentes"
    },
    {
      "type": "p",
      "text": "Micronora est né en 1949 sous la forme d’un « Salon national de l’horlogerie et des Industries Comtoises » ; l’association a changé de dénomination en 1969, lorsque le salon s’est ouvert aux microtechniques[^6]. Soixante-quinze ans plus tard, le fil rouge de l’édition 2024 était les **microtechniques intelligentes**, un thème décliné dans l’espace Zoom du salon[^6]."
    },
    {
      "type": "p",
      "text": "À l’inauguration, le 24 septembre, [Grand Besançon Métropole](https://www.grandbesancon.fr/album/micronora-2024/) présentait le rendez-vous comme un salon de 800 exposants, dont 33 % d’internationaux, représentant 41 pays[^2]. La presse locale faisait état du même ordre de grandeur, avec plus de 11 000 visiteurs professionnels attendus sur les quatre jours[^1] ; le bilan définitif de fréquentation n’avait pas encore été publié à la date de cet article. Le salon a été inauguré par Nicolas Dufourcq, directeur général de Bpifrance[^1], aux côtés d’Anne Vignot, maire de Besançon et présidente de Grand Besançon Métropole, de Marie-Guite Dufay, présidente de la Région Bourgogne-Franche-Comté, et de Thierry Bisiaux et Fanny Chauvin, président et directrice de Micronora[^2]."
    },
    {
      "type": "h2",
      "text": "Le palmarès des Microns d’Or"
    },
    {
      "type": "p",
      "text": "Le concours des Microns d’Or récompense des réalisations présentées pour la première fois dans le cadre du salon, dans trois catégories[^3]. Le palmarès 2024 est le suivant[^3] :"
    },
    {
      "type": "ol",
      "items": [
        "**Machines & biens d’équipement** : Micron d’Or pour **Chiron** et son centre d’usinage Micro5, doté d’une broche UGV pour l’usinage de haute précision ; Micron d’Argent pour Nidec et sa cellule Inline-Vision Cut & Weld, qui associe vision artificielle, découpe et raboutage automatiques.",
        "**Composants & sous-ensembles microtechniques** : Micron d’Or pour **SilMach** et son micromoteur pas à pas à cœur de silicium, présenté comme le plus petit et le plus autonome au monde ; Micron d’Argent pour VP Medtech x Armink, avec une cartouche d’aiguilles à tatouer à réservoir intégré.",
        "**Systèmes intelligents & logiciels** : Micron d’Or pour **Percipio Robotics** et son jumeau numérique pour la microfabrication."
      ]
    },
    {
      "type": "p",
      "text": "Le jury a en outre décerné une mention spéciale, hors catégorie, à **Laser Cheval** pour sa machine laser Quartz Géode, qui peut recevoir une source nanoseconde ou femtoseconde pour le micro-usinage laser, ou une source continue pour la soudure[^3]."
    },
    {
      "type": "p",
      "text": "Deux des trois Microns d’Or reviennent à des entreprises bisontines. [SilMach](/blog/ces-2026-startups-bourgogne-franche-comte-las-vegas), pionnière française de la micromécanique sur silicium, a reçu le sien dès le 24 septembre, pour ses solutions PowerMEMS destinées à motoriser des systèmes nomades comme les montres, l’électronique ou les dispositifs médicaux ; c’est son troisième Micron d’Or, après ceux de 2004 et 2014[^4]."
    },
    {
      "type": "h2",
      "text": "Un jumeau numérique primé dans un salon de mécaniciens"
    },
    {
      "type": "p",
      "text": "L’autre lauréat local, **[Percipio Robotics](/blog/vivatech-2026-percipio-robotics-startups-bfc)**, est spécialiste de la robotique de haute précision pour le micro-assemblage. L’entreprise faisait partie des exposants retenus pour [le Zoom](https://micronora.com/zoom-2024-les-applications-des-microtechniques-intelligentes/), l’animation phare du salon, consacrée cette année aux microtechniques intelligentes[^5][^6]."
    },
    {
      "type": "p",
      "text": "Elle devait y présenter « une plateforme de micromanipulation, le simulateur associé avec la possibilité d’agir sur celui-ci via une manette et le jumeau numérique d’une machine de démonstration avec deux grippers de micromanipulation »[^5]. Soit, concrètement, deux briques logicielles greffées sur le matériel[^5] :"
    },
    {
      "type": "ul",
      "items": [
        "un **simulateur** pilotable « par diverses interfaces (souris, joystick, manette…) », qui permet à l’utilisateur de simuler des mouvements, des actions et de contrôler des encombrements ;",
        "un **jumeau numérique** d’une machine de démonstration, décrit comme « un outil de suivi comportemental de la machine réelle » : via une interface graphique interactive, l’utilisateur voit ce qui se passe sur la machine, fait apparaître les informations dont il a besoin et agit sur certaines commandes."
      ]
    },
    {
      "type": "p",
      "text": "C’est ce jumeau numérique, présenté au concours comme « un outil polyvalent pour la microfabrication », qui a valu à Percipio Robotics le Micron d’Or de la catégorie « Systèmes intelligents & logiciels »[^3]. Autrement dit, la valeur ajoutée se déplace : le robot reste indispensable, mais la couche logicielle qui permet de le simuler, de le surveiller et de le comprendre devient un produit à part entière, primé au même titre qu’un centre d’usinage."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Micronora reste le rendez-vous international de la filière microtechnique du territoire. Voir un jumeau numérique conçu à Besançon récompensé aux côtés d’un centre d’usinage et d’un micromoteur en silicium confirme que l’écosystème local ne se limite plus à la précision mécanique : il produit aussi le logiciel qui la pilote. Pour les étudiants, les chercheurs et les entreprises du bassin, c’est un signal sur les compétences qui compteront demain."
    },
    {
      "type": "h2",
      "text": "Ce qu’il faut retenir"
    },
    {
      "type": "p",
      "text": "Pour ses 75 ans, Micronora annonçait à Micropolis plus de 800 exposants, dont un tiers d’internationaux, représentant 41 pays[^1][^2]. Son palmarès esquisse une trajectoire : Chiron pour la machine, SilMach pour le composant, Percipio Robotics pour le logiciel qui les fait dialoguer[^3]. Le bilan chiffré de l’édition, attendu dans les semaines qui viennent, dira si la fréquentation a suivi ; il restera ensuite à observer si la part des systèmes intelligents continue de croître dans les allées du salon."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Le salon Micronora 2024, inauguré par Nicolas Dufourcq, directeur général de BPi France",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/le-salon-micronora-2024-inaugure-par-nicolas-dufourcq-directeur-general-de-bpi-france/",
      "type": "press",
      "date": "2024-09-24"
    },
    {
      "id": 2,
      "title": "Micronora 2024 (album photo de l’inauguration)",
      "publisher": "Grand Besançon Métropole",
      "url": "https://www.grandbesancon.fr/album/micronora-2024/",
      "type": "official",
      "date": "2024-09-25"
    },
    {
      "id": 3,
      "title": "Concours Microns et Nano d’Or – Récompenser l’innovation",
      "publisher": "Micronora",
      "url": "https://micronora.com/concours-microns-or/",
      "type": "official",
      "date": ""
    },
    {
      "id": 4,
      "title": "Microtechniques : la société bisontine SilMach reçoit le Micron d’or 2024 au salon Micronora",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/microtechniques-la-societe-bisontine-silmach-recoit-le-micron-dor-2024-au-salon-micronora/",
      "type": "press",
      "date": "2024-09-24"
    },
    {
      "id": 5,
      "title": "Zoom 2024 : les applications des microtechniques intelligentes",
      "publisher": "Micronora",
      "url": "https://micronora.com/zoom-2024-les-applications-des-microtechniques-intelligentes/",
      "type": "official",
      "date": "2024-09-01"
    },
    {
      "id": 6,
      "title": "En 2024, le salon Micronora fêtera ses 75 ans, « l’heure du renouveau »",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/le-salon-micronora-fetera-ses-75-ans-lheure-du-renouveau/",
      "type": "press",
      "date": "2024-04-16"
    }
  ]
};
